/**
 * Adapter from the shell's real modules to the shim interface. To integrate, replace the body of ./index.ts with
 * `export * from "./shell";` (or point the feature's imports here). Nothing else in the feature changes.
 *
 * The shell's `api` is nested (api.network.matches()); the feature uses the flat names below.
 */
import { platformForHost, useSession as useShellSession } from "@/app/session";
import { useOpenContact as useShellOpenContact } from "@/app/navigation";
import { api as shellApi } from "@/api/client";
import type { AskFeedbackRequest, ConnectionRequestBody, CreateContactShareRequest, DirectoryQuery, FacilitateRequest, Id, InviteToHubRequest, LinkContactsRequest, MatchActionRequest, RequestIntroductionRequest, RespondConnectionBody, RespondShareBody, SendMessageRequest, StartConversationRequest, UpdateConnectionBody, UpsertNeedsRequest, UpsertServicesRequest } from "@hub/shared";

export { engineKey, invalidateEngine, invokeEngine, useEngineMutation, useEngineQuery } from "@/engine/hooks";
export { ContactChip } from "@/features/people/ContactChip";

export function useSession() {
  const s = useShellSession();
  return { user: s.user, workspaceId: s.workspaceId, token: s.token, serverUrl: s.serverUrl, isSignedIn: s.isSignedIn, signOut: s.signOut };
}

/** Which app a rating came from, for the owner's Feedback tab: the Mac app or a browser. */
export function feedbackPlatform(): "mac" | "web" {
  return platformForHost() === "darwin" ? "mac" : "web";
}

export function useOpenContact(): (contactId: string) => void {
  const open = useShellOpenContact();
  return (contactId: string) => open(contactId);
}

export const api = {
  directory: (q: DirectoryQuery = {}) => shellApi.network.directory(q),
  connections: () => shellApi.network.connections.list(),
  requestConnection: (body: ConnectionRequestBody) => shellApi.network.connections.request(body),
  respondConnection: (id: Id, body: RespondConnectionBody) => shellApi.network.connections.respond(id, body),
  updateConnection: (id: Id, body: UpdateConnectionBody) => shellApi.network.connections.update(id, body),
  putNeeds: (body: UpsertNeedsRequest) => shellApi.network.upsertNeeds(body),
  putServices: (body: UpsertServicesRequest) => shellApi.network.upsertServices(body),
  matches: () => shellApi.network.matches(),
  discover: () => shellApi.network.discover(),
  matchAction: (id: Id, body: MatchActionRequest) => shellApi.network.matchAction(id, body),
  opportunities: () => shellApi.network.opportunities(),
  facilitate: (body: FacilitateRequest) => shellApi.network.facilitate(body),
  requestIntroduction: (body: RequestIntroductionRequest) => shellApi.network.requestIntroduction(body),
  shares: () => shellApi.network.shares.list(),
  createShare: (body: CreateContactShareRequest) => shellApi.network.shares.create(body),
  respondShare: (id: Id, body: RespondShareBody) => shellApi.network.shares.respond(id, body),
  conversations: () => shellApi.conversations.list(),
  startConversation: (body: StartConversationRequest) => shellApi.conversations.start(body),
  messages: (id: Id) => shellApi.conversations.messages(id),
  sendMessage: (id: Id, body: SendMessageRequest) => shellApi.conversations.send(id, body),
  credits: () => shellApi.credits(),
  inviteToHub: (body: InviteToHubRequest) => shellApi.invites.toHub(body),
  linkContacts: (body: LinkContactsRequest) => shellApi.network.linkContacts(body),
  peopleYouKnow: () => shellApi.network.peopleYouKnow(),
  dismissPersonYouKnow: (userId: Id) => shellApi.network.dismissPersonYouKnow(userId),
  block: (userId: Id) => shellApi.network.block(userId),
  report: (body: Parameters<typeof shellApi.network.report>[0]) => shellApi.network.report(body),
  askFeedback: (body: AskFeedbackRequest) => shellApi.feedback.ask(body),
};
